import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';

import LoadingIndicator from '@/components/LoadingIndicator.js';
import CustomModal from '@/components/CustomModal.js';
import {
  ALIYUN_BUILT_IN_FONT_OPTIONS,
  VIDEO_PRODUCTION_ADAPT_TYPE_OPTIONS,
  VIDEO_PRODUCTION_JOB_STATUS,
  VIDEO_PRODUCTION_SLOT_KIND,
} from '../../constants.js';
import {
  fetchOrderAutomation,
  submitOrderVideoProductionJob,
} from '@/features/automation/videoEditing/services/productionJobService.js';
import { fetchTask } from '@/features/orders/ordering/services/orderWorkflowService.js';
import {
  appendGroupAsset,
  buildAssetCategoryFilters,
  buildProductionSlotEntries,
  buildSlotValue,
  filterAssetCandidatesByCategory,
  formatDefaultContentHint,
  getIncompleteImageGroups,
  getGroupSelectedItems,
  getTextGroupMetrics,
  getTextInputMetrics,
  getGroupTextValue,
  getSlotAdaptTypeValue,
  getSlotFontFileValue,
  getSlotSelectedValue,
  isMediaSlotKind,
  moveGroupAsset,
  normalizeMaterialFontAssets,
  normalizeMaterialAssets,
  normalizeOrderImageAssets,
  normalizeOrderVideoAssets,
  prioritizeSelectedAssets,
  removeGroupAsset,
  toggleSingleSlotAsset,
  toggleGroupAsset,
  updateGroupTextValue,
  updateSlotValueConfig,
  validateSlotValues,
  VIDEO_PRODUCTION_SLOT_ENTRY_TYPE,
} from '../../utils/productionSlotUtils.js';
import {
  buildVideoProductionJobsPath,
  VIDEO_PRODUCTION_ORDER_ID_PARAM,
} from '../../utils/productionEntryUtils.js';
import {
  canUseProductionAutomation,
} from '../../../utils/productionAccess.js';
import {
  fetchMaterialCategories,
  fetchVideoMaterials,
} from '../../services/materialService.js';
import ProductionAssetPicker from './ProductionAssetPicker.js';
import './ProductionCreatePage.scss';

const SHOW_PRODUCTION_RUNTIME_CONFIG = false;
const TEXT_GROUP_WITH_DEFAULT_CONTENT_HINT =
  '整组未填写时使用插槽默认文字；填写后空行或未覆盖的插槽保持为空';
const TEXT_GROUP_WITHOUT_DEFAULT_CONTENT_HINT =
  '整组未填写时不显示；填写后空行或未覆盖的插槽保持为空';
const EMPTY_IMAGE_GROUP_PROMPT = {
  groupKey: '',
  repeatMode: false,
};

const getTemplates = (automation) =>
  automation?.templates || (automation?.template ? [automation.template] : []);

const AssetCategoryTabs = ({
  filters,
  selectedCategoryId,
  disabled,
  onSelect,
}) => {
  if (!filters.length || filters.length === 1) return null;

  return (
    <div className="video-production-create-page__category-tabs">
      {filters.map((filter) => (
        <button
          key={filter.id || 'all'}
          type="button"
          className={selectedCategoryId === filter.id ? 'is-active' : ''}
          disabled={disabled}
          onClick={() => onSelect(filter.id)}
        >
          {filter.name}
        </button>
      ))}
    </div>
  );
};

const SlotRuntimeConfigControl = ({
  disabled,
  fontOptions,
  onConfigChange,
  slot,
  slotValues,
}) => {
  if (isMediaSlotKind(slot.kind)) {
    return (
      <label className="slot-config-control">
        <span>适配</span>
        <select
          value={getSlotAdaptTypeValue(slot, slotValues)}
          disabled={disabled}
          onChange={(event) =>
            onConfigChange(slot, 'adapt_type', event.target.value)
          }
        >
          {VIDEO_PRODUCTION_ADAPT_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (slot.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT) {
    const selectedFontFile = getSlotFontFileValue(slot, slotValues);
    const options = [...fontOptions];
    if (
      selectedFontFile &&
      !options.some((option) => option.value === selectedFontFile)
    ) {
      options.push({ value: selectedFontFile, label: selectedFontFile });
    }

    return (
      <label className="slot-config-control">
        <span>字体</span>
        <select
          value={selectedFontFile}
          disabled={disabled}
          onChange={(event) =>
            onConfigChange(slot, 'font_file', event.target.value)
          }
        >
          <option value="">模板默认</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return null;
};

const VideoUrlFallback = ({
  slot,
  selectedValue,
  disabled,
  onInputChange,
}) => (
  <div className="video-production-create-page__video-fallback">
    <input
      value={selectedValue || ''}
      onChange={(event) => onInputChange(slot, event.target.value)}
      placeholder="输入 OSS 视频 URL"
      disabled={disabled}
    />
    <div className="video-production-create-page__hint">
      当前没有可选视频素材时，可以临时输入 OSS 视频 URL。
    </div>
  </div>
);

const getGroupItemValue = (kind, item = {}) => {
  const keys =
    kind === VIDEO_PRODUCTION_SLOT_KIND.IMAGE
      ? ['image_id', 'media_url', 'oss_url', 'url', 'image_url']
      : ['oss_url', 'media_url', 'url'];
  return (
    keys
      .map((key) => item[key])
      .find((value) => value !== undefined && value !== null && value !== '') || ''
  );
};

const ProductionCreatePage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get(VIDEO_PRODUCTION_ORDER_ID_PARAM) || '';
  const navigate = useNavigate();
  const role = Number(sessionStorage.getItem('role_id'));
  const [clientInfo, setClientInfo] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [slotValues, setSlotValues] = useState({});
  const [slotCategoryFilters, setSlotCategoryFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [imageGroupPrompt, setImageGroupPrompt] = useState(
    EMPTY_IMAGE_GROUP_PROMPT
  );

  const canUseVideoProduction = canUseProductionAutomation(role);

  const loadPage = useCallback(async () => {
    if (!canUseVideoProduction || !orderId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const [taskData, automationData, categoriesData, materialsData] =
        await Promise.all([
          fetchTask(orderId),
          fetchOrderAutomation(orderId),
          fetchMaterialCategories(),
          fetchVideoMaterials({ is_active: true, limit: 200 }),
        ]);
      setClientInfo(taskData);
      setAutomation(automationData);
      setMaterialCategories(categoriesData);
      setMaterials(materialsData);
    } catch (error) {
      setMessage(error.response?.data?.message || '读取自动化生成配置失败');
    } finally {
      setLoading(false);
    }
  }, [canUseVideoProduction, orderId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const templates = getTemplates(automation);
    setSelectedTemplateId(templates.length === 1 ? templates[0].id : '');
    setSlotValues({});
    setSlotCategoryFilters({});
    setImageGroupPrompt(EMPTY_IMAGE_GROUP_PROMPT);
  }, [automation]);

  const templates = getTemplates(automation);
  const selectedTemplate = useMemo(
    () =>
      templates.find((item) => item.id === selectedTemplateId) ||
      (templates.length === 1 ? templates[0] : null),
    [selectedTemplateId, templates]
  );
  const slotConfig = useMemo(
    () => selectedTemplate?.slot_config || [],
    [selectedTemplate]
  );
  const slotGroupConfig = useMemo(
    () => selectedTemplate?.slot_group_config || {},
    [selectedTemplate]
  );
  const slotEntries = useMemo(
    () => buildProductionSlotEntries({ slotConfig, slotGroupConfig }),
    [slotConfig, slotGroupConfig]
  );
  const imageAssets = useMemo(
    () => [
      ...normalizeOrderImageAssets(clientInfo?.images || []),
      ...normalizeMaterialAssets(materials, VIDEO_PRODUCTION_SLOT_KIND.IMAGE),
    ],
    [clientInfo?.images, materials]
  );
  const videoAssets = useMemo(
    () => [
      ...normalizeOrderVideoAssets(clientInfo?.videos || clientInfo?.video_assets || []),
      ...normalizeMaterialAssets(materials, VIDEO_PRODUCTION_SLOT_KIND.VIDEO),
    ],
    [clientInfo?.video_assets, clientInfo?.videos, materials]
  );
  const customFontOptions = useMemo(
    () =>
      normalizeMaterialFontAssets(materials).map((font) => ({
        value: font.value,
        label: `自定义：${font.label}`,
      })),
    [materials]
  );
  const fontOptions = useMemo(() => {
    const options = [
      ...ALIYUN_BUILT_IN_FONT_OPTIONS,
      ...customFontOptions,
    ];
    const seen = new Set();
    return options.filter((option) => {
      if (!option.value || seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [customFontOptions]);
  const promptedImageGroup = useMemo(
    () =>
      slotEntries.find(
        (entry) =>
          entry.type === VIDEO_PRODUCTION_SLOT_ENTRY_TYPE.GROUP &&
          entry.group.key === imageGroupPrompt.groupKey
      )?.group || null,
    [imageGroupPrompt.groupKey, slotEntries]
  );
  const promptedImageGroupItems = promptedImageGroup
    ? getGroupSelectedItems(slotValues, promptedImageGroup.key)
    : [];
  const promptedImageGroupSelectedValues = promptedImageGroupItems.map((item) =>
    getGroupItemValue(VIDEO_PRODUCTION_SLOT_KIND.IMAGE, item)
  );
  const promptedImageGroupMissingCount = promptedImageGroup
    ? Math.max(
        promptedImageGroup.slots.length - promptedImageGroupItems.length,
        0
      )
    : 0;
  const repeatImageCandidates = promptedImageGroup
    ? prioritizeSelectedAssets({
        allAssets: imageAssets,
        visibleAssets: [],
        selectedValues: promptedImageGroupSelectedValues,
      })
    : [];

  const videoStandardName =
    automation?.videoType?.title ||
    automation?.title ||
    clientInfo?.videoType ||
    clientInfo?.video_type ||
    '';

  const selectTemplate = (templateId) => {
    if (String(templateId) === String(selectedTemplateId)) {
      return;
    }

    setSelectedTemplateId(templateId);
    setSlotValues({});
    setSlotCategoryFilters({});
    setMessage('');
    setImageGroupPrompt(EMPTY_IMAGE_GROUP_PROMPT);
  };

  const updateSlotValue = (slot, value) => {
    setSlotValues((current) => ({
      ...current,
      [slot.key]: buildSlotValue(
        slot,
        value,
        undefined,
        current?.[slot.key]?.config
      ),
    }));
  };

  const updateSlotConfig = (slot, configKey, value) => {
    setSlotValues((current) =>
      updateSlotValueConfig({
        slotValues: current,
        slot,
        configKey,
        value,
      })
    );
  };

  const toggleAsset = (slot, asset) => {
    setSlotValues((current) =>
      toggleSingleSlotAsset({
        slotValues: current,
        slot,
        assetId: asset.value,
        valueKey: asset.valueKey,
      })
    );
  };

  const toggleGroupAssetSelection = (group, asset, selectedIndex) => {
    setSlotValues((current) =>
      Number.isInteger(selectedIndex)
        ? removeGroupAsset({
            slotValues: current,
            group,
            itemIndex: selectedIndex,
          })
        : toggleGroupAsset({
            slotValues: current,
            group,
            asset,
          })
    );
  };

  const appendRepeatedGroupAsset = (asset) => {
    if (!promptedImageGroup) return;
    setSlotValues((current) =>
      appendGroupAsset({
        slotValues: current,
        group: promptedImageGroup,
        asset,
      })
    );
  };

  const closeImageGroupPrompt = () => {
    setImageGroupPrompt(EMPTY_IMAGE_GROUP_PROMPT);
  };

  const handleImageGroupPromptConfirm = () => {
    if (!imageGroupPrompt.repeatMode) {
      setImageGroupPrompt((current) => ({
        ...current,
        repeatMode: true,
      }));
      return;
    }
    if (promptedImageGroupMissingCount === 0) {
      closeImageGroupPrompt();
    }
  };

  const moveSelectedGroupAsset = useCallback(
    (group) =>
      ({ fromIndex, toIndex }) => {
        setSlotValues((current) =>
          moveGroupAsset({
            slotValues: current,
            group,
            fromIndex,
            toIndex,
          })
        );
      },
    []
  );

  const updateGroupText = (group, text) => {
    setSlotValues((current) =>
      updateGroupTextValue({
        slotValues: current,
        group,
        text,
      })
    );
  };

  const changeSlotCategory = (slotKey, categoryId) => {
    setSlotCategoryFilters((current) => ({
      ...current,
      [slotKey]: categoryId,
    }));
  };

  const handleSubmit = async () => {
    const [incompleteImageGroup] = getIncompleteImageGroups({
      slotConfig,
      slotGroupConfig,
      slotValues,
    });
    if (incompleteImageGroup) {
      setMessage('');
      setImageGroupPrompt({
        groupKey: incompleteImageGroup.key,
        repeatMode: false,
      });
      return;
    }

    const validationError = validateSlotValues({
      slotConfig,
      slotGroupConfig,
      slotValues,
    });
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setSubmitting(true);
    setMessage('');
    try {
      const job = await submitOrderVideoProductionJob(
        orderId,
        slotValues,
        selectedTemplateId
      );
      navigate(buildVideoProductionJobsPath(job.id));
    } catch (error) {
      setMessage(error.response?.data?.message || '提交视频生成任务失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingIndicator label="正在读取自动化生成配置" />;
  }

  if (!orderId) {
    return (
      <div className="video-production-create-page">
        <div className="video-production-create-page__message">
          缺少订单 ID，无法进入自动化制作
        </div>
      </div>
    );
  }

  if (!canUseVideoProduction) {
    return (
      <div className="video-production-create-page">
        <div className="video-production-create-page__message">
          当前账号无权使用自动化视频生成
        </div>
      </div>
    );
  }

  return (
    <div className="video-production-create-page">
      {submitting && (
        <div className="video-production-create-page__busy-overlay">
          <LoadingIndicator label="正在提交生成任务，请等待" border />
        </div>
      )}

      <CustomModal
        show={Boolean(promptedImageGroup)}
        onHide={closeImageGroupPrompt}
        title={promptedImageGroup?.name || '图片组素材不足'}
        body={(
          <div className="image-group-shortage">
            <p>
              需要 {promptedImageGroup?.slots.length || 0} 张，已选择{' '}
              {promptedImageGroupItems.length} 张，还差{' '}
              {promptedImageGroupMissingCount} 张。
            </p>
            {imageGroupPrompt.repeatMode ? (
              <>
                <span>点击下方已选照片可追加一次，同一张照片可重复点击。</span>
                <ProductionAssetPicker
                  allAssets={imageAssets}
                  visibleAssets={repeatImageCandidates}
                  disabled={submitting}
                  emptyText="当前没有可重复使用的照片"
                  maxReached={promptedImageGroupMissingCount === 0}
                  onToggle={appendRepeatedGroupAsset}
                />
              </>
            ) : (
              <span>
                {promptedImageGroupItems.length > 0
                  ? '可以重复使用已选照片，或返回继续选择其他照片。'
                  : '请返回并至少选择一张照片后，再选择重复照片。'}
              </span>
            )}
          </div>
        )}
        confirmText={imageGroupPrompt.repeatMode ? '完成' : '选择重复照片'}
        cancelText="继续选择"
        onConfirm={handleImageGroupPromptConfirm}
        confirmBtnDisabled={
          imageGroupPrompt.repeatMode
            ? promptedImageGroupMissingCount > 0
            : promptedImageGroupItems.length === 0
        }
        size={imageGroupPrompt.repeatMode ? 'lg' : undefined}
      />

      <div className="video-production-create-page__header">
        <div>
          <h2>自动化视频生成</h2>
          <p>
            {clientInfo?.clientName || orderId}
            {videoStandardName ? ` / ${videoStandardName}` : ''}
          </p>
        </div>
        <div className="video-production-create-page__actions">
          <Button
            variant="outline-secondary"
            onClick={() => navigate(`/review/${orderId}`)}
            disabled={submitting}
          >
            返回订单详情
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate(buildVideoProductionJobsPath())}
            disabled={submitting}
          >
            任务管理
          </Button>
        </div>
      </div>

      {message && (
        <div className="video-production-create-page__message">{message}</div>
      )}

      {!automation?.supported && (
        <div className="video-production-create-page__empty-panel">
          该视频标准暂不支持自动化生成
        </div>
      )}

      {automation?.supported && (
        <div className="video-production-create-page__layout">
          <aside className="video-production-create-page__templates">
            <h3>选择模板</h3>
            <div className="video-production-create-page__template-list">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className={`template-card ${
                    selectedTemplateId === template.id ? 'is-selected' : ''
                  }`}
                  onClick={() => selectTemplate(template.id)}
                  disabled={submitting}
                >
                  <span>{template.name}</span>
                  <strong>{template.slot_config?.length || 0} 个插槽</strong>
                </button>
              ))}
            </div>
          </aside>

          <main className="video-production-create-page__slots">
            {!selectedTemplate && (
              <div className="video-production-create-page__empty-panel">
                请先选择一个自动化模板
              </div>
            )}

            {selectedTemplate && slotConfig.length === 0 && (
              <div className="video-production-create-page__empty-panel">
                当前模板没有可用插槽，请先在模板管理中解析插槽
              </div>
            )}

            {selectedTemplate &&
              slotEntries.map((entry) => {
                if (entry.type === VIDEO_PRODUCTION_SLOT_ENTRY_TYPE.GROUP) {
                  const group = entry.group;
                const groupAssets =
                  group.kind === VIDEO_PRODUCTION_SLOT_KIND.IMAGE
                    ? imageAssets
                    : videoAssets;
                const groupCategoryKey = `group:${group.key}`;
                const selectedCategoryId =
                  slotCategoryFilters[groupCategoryKey] || '';
                const categoryFilters = buildAssetCategoryFilters(
                  groupAssets,
                  materialCategories
                );
                const filteredAssets = filterAssetCandidatesByCategory(
                  groupAssets,
                  selectedCategoryId
                );
                const selectedItems = getGroupSelectedItems(slotValues, group.key);
                const selectedValues = selectedItems.map((item) =>
                  getGroupItemValue(group.kind, item)
                );
                const maxReached = selectedItems.length >= group.slots.length;
                const groupMissingCount = Math.max(
                  group.slots.length - selectedItems.length,
                  0
                );
                const groupTextValue = getGroupTextValue(slotValues, group.key);
                const groupTextMetrics = getTextGroupMetrics(groupTextValue);
                const groupMaxLength = Number(group.runtime?.max_length || 0);
                const groupDefaultContents = group.slots.map(
                  (slot) => slot.default_content
                );
                const hasGroupDefaultContent = groupDefaultContents.some(
                  (content) => String(content || '').trim()
                );

                return (
                  <section className="slot-section slot-section--group" key={group.key}>
                    <div className="slot-section__header">
                      <div>
                        <h3>{group.name || group.key}</h3>
                        <span>
                          {group.kind} / {group.slots.length} 个插槽
                        </span>
                      </div>
                      <div className="slot-section__header-actions">
                        <strong>
                          {formatDefaultContentHint(groupDefaultContents)}
                        </strong>
                      </div>
                    </div>

                    {isMediaSlotKind(group.kind) && (
                      <>
                        <AssetCategoryTabs
                          filters={categoryFilters}
                          selectedCategoryId={selectedCategoryId}
                          disabled={submitting}
                          onSelect={(categoryId) =>
                            changeSlotCategory(groupCategoryKey, categoryId)
                          }
                        />
                        <ProductionAssetPicker
                          allAssets={groupAssets}
                          visibleAssets={filteredAssets}
                          selectedValues={selectedValues}
                          disabled={submitting}
                          emptyText="当前没有可选素材"
                          maxReached={maxReached}
                          onMove={moveSelectedGroupAsset(group)}
                          onToggle={(asset, selectedIndex) =>
                            toggleGroupAssetSelection(group, asset, selectedIndex)
                          }
                        />
                        {group.kind === VIDEO_PRODUCTION_SLOT_KIND.IMAGE && (
                          <div className={`image-group-selection-count ${
                            groupMissingCount > 0 ? 'is-incomplete' : 'is-complete'
                          }`}>
                            已选择 {selectedItems.length} 张 / 需要{' '}
                            {group.slots.length} 张
                            {groupMissingCount > 0
                              ? `，还差 ${groupMissingCount} 张`
                              : '，已选满'}
                          </div>
                        )}
                      </>
                    )}

                    {group.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT && (
                      <>
                        <textarea
                          value={groupTextValue}
                          onChange={(event) =>
                            updateGroupText(group, event.target.value)
                          }
                          placeholder="输入整段文字，按行分割，每行对应一个插槽"
                          rows={5}
                          disabled={submitting}
                        />
                        <div className={`text-input-metrics ${
                          groupMaxLength > 0 &&
                          groupTextMetrics.longestSegmentLength > groupMaxLength
                            ? 'is-over-limit'
                            : ''
                        }`}>
                          已输入 {groupTextMetrics.segmentCount} 行 / 对应 {group.slots.length} 个插槽
                        </div>
                        <div className="video-production-create-page__hint">
                          {hasGroupDefaultContent
                            ? TEXT_GROUP_WITH_DEFAULT_CONTENT_HINT
                            : TEXT_GROUP_WITHOUT_DEFAULT_CONTENT_HINT}
                        </div>
                      </>
                    )}
                  </section>
                );
                }

                const slot = entry.slot;
                const selectedValue = getSlotSelectedValue(slot, slotValues);
                const slotAssets =
                  slot.kind === VIDEO_PRODUCTION_SLOT_KIND.IMAGE
                    ? imageAssets
                    : videoAssets;
                const selectedCategoryId = slotCategoryFilters[slot.key] || '';
                const categoryFilters = buildAssetCategoryFilters(
                  slotAssets,
                  materialCategories
                );
                const filteredAssets = filterAssetCandidatesByCategory(
                  slotAssets,
                  selectedCategoryId
                );
                const textMetrics = getTextInputMetrics(selectedValue);
                const slotMaxLength = Number(slot.runtime?.max_length || 0);

                return (
                  <section className="slot-section" key={slot.key}>
                    <div className="slot-section__header">
                      <div>
                        <h3>{slot.key}</h3>
                        {slot.description && <span>{slot.description}</span>}
                      </div>
                      <div className="slot-section__header-actions">
                        {/* 暂时隐藏制作页运行时配置，保留组件和状态更新逻辑。 */}
                        {SHOW_PRODUCTION_RUNTIME_CONFIG && (
                          <SlotRuntimeConfigControl
                            disabled={submitting}
                            fontOptions={fontOptions}
                            onConfigChange={updateSlotConfig}
                            slot={slot}
                            slotValues={slotValues}
                          />
                        )}
                        <strong>
                          {formatDefaultContentHint(slot.default_content)}
                        </strong>
                      </div>
                    </div>

                    {slot.kind === VIDEO_PRODUCTION_SLOT_KIND.IMAGE && (
                      <>
                        <AssetCategoryTabs
                          filters={categoryFilters}
                          selectedCategoryId={selectedCategoryId}
                          disabled={submitting}
                          onSelect={(categoryId) =>
                            changeSlotCategory(slot.key, categoryId)
                          }
                        />
                        <ProductionAssetPicker
                          allAssets={slotAssets}
                          visibleAssets={filteredAssets}
                          selectedValue={selectedValue}
                          disabled={submitting}
                          emptyText="当前没有可选图片素材"
                          onToggle={(asset) => toggleAsset(slot, asset)}
                        />
                      </>
                    )}

                    {slot.kind === VIDEO_PRODUCTION_SLOT_KIND.VIDEO && (
                      videoAssets.length > 0 ? (
                        <>
                          <AssetCategoryTabs
                            filters={categoryFilters}
                            selectedCategoryId={selectedCategoryId}
                            disabled={submitting}
                            onSelect={(categoryId) =>
                              changeSlotCategory(slot.key, categoryId)
                            }
                          />
                          <ProductionAssetPicker
                            allAssets={slotAssets}
                            visibleAssets={filteredAssets}
                            selectedValue={selectedValue}
                            disabled={submitting}
                            emptyText="当前分类没有可选视频素材"
                            onToggle={(asset) => toggleAsset(slot, asset)}
                          />
                        </>
                      ) : (
                        <VideoUrlFallback
                          slot={slot}
                          selectedValue={selectedValue}
                          disabled={submitting}
                          onInputChange={updateSlotValue}
                        />
                      )
                    )}

                    {slot.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT && (
                      <>
                        <textarea
                          value={selectedValue}
                          onChange={(event) => updateSlotValue(slot, event.target.value)}
                          placeholder="可选；不填则使用模板配置页面默认内容，如为配置默认内容则为空"
                          rows={4}
                          disabled={submitting}
                        />
                        <div className={`text-input-metrics ${
                          slotMaxLength > 0 &&
                          textMetrics.characterCount > slotMaxLength
                            ? 'is-over-limit'
                            : ''
                        }`}>
                          当前 {textMetrics.characterCount} 字
                          {slotMaxLength > 0 ? ` / 最多 ${slotMaxLength} 字` : ''}
                        </div>
                      </>
                    )}
                  </section>
                );
              })}

            {selectedTemplate && slotConfig.length > 0 && (
              <div className="video-production-create-page__submit-bar">
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={submitting || !selectedTemplateId}
                >
                  {submitting
                    ? VIDEO_PRODUCTION_JOB_STATUS.SUBMITTING
                    : '一键生成'}
                </Button>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default ProductionCreatePage;
